// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * A map component for visualizing asset locations.
 * This module provides a visual representation of an asset's
 * current location on a map, with support for missing location
 * data and transit states. It integrates with the location map
 * component and provides appropriate fallback displays.
 *
 * Key features:
 * - Location visualization
 * - Transit state handling
 * - Missing data fallbacks
 * - Responsive sizing
 * - Visual overlays
 */
import { Asset } from '@/asset/api/types';
import { StaticLocationMap } from '@/location/components/map';
import { SparseLocation } from '@/sparse/api/types';
import { Paper, Typography, alpha, styled } from '@mui/material';

/**
 * Styled container component for the map.
 * Provides consistent styling and layout.
 */
const Container = styled(Paper)(({ theme }) => ({
  overflow: 'hidden',
  position: 'relative',
  boxShadow: theme.shadows[6],
}));

/**
 * Styled overlay component for status messages.
 * Displays when location is missing or invalid.
 */
const Overlay = styled(Typography)(({ theme }) => ({
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 1,
  display: 'flex',
  position: 'absolute',
  textAlign: 'center',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(6),
  color: theme.palette.text.secondary,
  backgroundColor: alpha(theme.palette.background.paper, 0.8),
}));

/**
 * Extracts coordinates from an asset's current location.
 * Returns null if location is missing or coordinates are invalid.
 *
 * @param asset - The asset to get coordinates from
 * @returns Coordinates object or null if invalid
 */
function getCoordinates(asset: Asset) {
  if (asset.currentLocation === null) {
    return null;
  }

  // For zones, use the parent location's coordinates since zones don't have their own
  const coordSource =
    asset.currentLocation.type === 'ZONE' &&
    asset.currentLocation.parentLocation
      ? asset.currentLocation.parentLocation
      : asset.currentLocation;

  if (coordSource.latitude === null || coordSource.longitude === null) {
    return null;
  }

  return {
    latitude: Number(coordSource.latitude),
    longitude: Number(coordSource.longitude),
  };
}

/**
 * Props for configuring the AssetMap component.
 *
 * @interface Props
 */
interface Props {
  /**
   * The asset to display on the map.
   * Used to determine location and status.
   */
  asset: Asset;

  /**
   * Width of the map container.
   * Can be number (pixels) or string (CSS value).
   */
  width: string | number;

  /**
   * Height of the map container.
   * Can be number (pixels) or string (CSS value).
   */
  height: string | number;
}

/**
 * A map component for visualizing asset locations.
 * Displays an asset's current location on a map, with
 * appropriate fallbacks for missing or invalid location
 * data. Shows overlays for transit or invalid states.
 *
 * Features:
 * - Location visualization
 * - Transit state handling
 * - Missing data fallbacks
 * - Responsive sizing
 * - Visual overlays
 * - Consistent styling
 *
 * Usage example:
 * ```tsx
 * function AssetDetails({ asset }: Props) {
 *   return (
 *     <AssetMap
 *       asset={asset}
 *       width={600}
 *       height={400}
 *     />
 *   );
 * }
 * ```
 */
export function AssetMap(props: Props) {
  // Prepare location data for map
  const locations: SparseLocation[] = [];
  const coordinates = getCoordinates(props.asset);

  if (props.asset.currentLocation) {
    locations.push(props.asset.currentLocation);
  }

  return (
    <Container
      variant="outlined"
      sx={{ width: props.width, height: props.height }}
    >
      {(props.asset.currentLocation === null || coordinates === null) && (
        <Overlay variant="body2" color="text.secondary">
          {props.asset.currentLocation === null
            ? 'Asset in transit'
            : 'No map address found for this location'}
        </Overlay>
      )}
      <StaticLocationMap center={coordinates} locations={locations} />
    </Container>
  );
}
